if (!customElements.get('budetz-style-finder')) {
  class BudetzStyleFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.intro = this.querySelector('[data-bsf-intro]');
      this.quiz = this.querySelector('[data-bsf-quiz]');
      this.result = this.querySelector('[data-bsf-result]');
      this.questions = Array.from(this.querySelectorAll('[data-bsf-question]'));
      this.startButton = this.querySelector('[data-bsf-start]');
      this.backButton = this.querySelector('[data-bsf-back]');
      this.nextButton = this.querySelector('[data-bsf-next]');
      this.restartButton = this.querySelector('[data-bsf-restart]');
      this.progress = this.querySelector('[data-bsf-progress]');
      this.progressBar = this.querySelector('[data-bsf-progress-bar]');
      this.progressName = this.querySelector('[data-bsf-progress-name]');
      this.progressCount = this.querySelector('[data-bsf-progress-count]');
      this.globalStatus = this.querySelector('[data-bsf-global-status]');
      this.emailForm = this.querySelector('.budetz-style-email-form');
      this.emailConsent = this.querySelector('[data-bsf-email-consent]');
      this.emailClientError = this.querySelector('[data-bsf-email-client-error]');
      this.emailSuccess = this.querySelector('[data-bsf-email-success]');
      this.emailFields = this.querySelector('[data-bsf-email-fields]');
      this.resultGallery = this.querySelector('[data-bsf-result-gallery]');
      this.resultGalleryTrack = this.querySelector('[data-bsf-result-gallery-track]');
      this.isPopup = this.dataset.displayMode === 'popup';
      this.popup = this.querySelector('[data-bsf-popup]');
      this.dialog = this.querySelector('[data-bsf-dialog]');
      this.dialogScroll = this.querySelector('[data-bsf-dialog-scroll]');
      this.popupOpenButton = this.querySelector('[data-bsf-popup-open]');
      this.popupCloseButtons = Array.from(this.querySelectorAll('[data-bsf-popup-close]'));
      this.popupDismissButton = this.querySelector('[data-bsf-popup-dismiss]');
      this.popupIsOpen = false;
      this.lastFocusedElement = null;
      this.autoOpenTimer = null;
      this.dismissStorageKey = 'budetz_style_finder_popup_dismissed_until_v2';
      this.currentRecommendationGroups = null;
      this.isEmailSubmitting = false;

      this.state = {
        step: 0,
        impressions: [],
        intensity: '',
        occasion: '',
        weather: '',
      };

      this.storageKey = `budetzStyleFinder:${this.dataset.sectionId || 'page'}`;
      this.profileStorageKey = 'budetz_style_finder';
      this.completedStorageKey = 'budetz_style_finder_completed';

      try {
        const configNode = this.querySelector('[data-bsf-config]');
        const productsNode = this.querySelector('[data-bsf-products]');

        this.config = JSON.parse(configNode.textContent);
        this.products = productsNode
          ? JSON.parse(productsNode.textContent)
          : [];

       if (!Array.isArray(this.products)) {
         this.products = [];
       }
      } catch (error) {
       this.disableWithError(
         'The Style Finder could not load. Please refresh the page and try again.'
       );
        return;
      }

      this.loadProfileState();
      this.restoreInputsFromState();

      this.startButton?.addEventListener('click', () => this.showQuestion(0));
      this.backButton?.addEventListener('click', () => this.goBack());
      this.nextButton?.addEventListener('click', () => this.goNext());
      this.restartButton?.addEventListener('click', () => this.restart());
      this.addEventListener('change', (event) => this.handleAnswerChange(event));
      this.emailForm?.addEventListener('submit', (event) => this.handleEmailSubmit(event));
      this.popupOpenButton?.addEventListener('click', () => this.openPopup());
      this.popupCloseButtons.forEach((button) => button.addEventListener('click', () => this.closePopup()));
      this.popupDismissButton?.addEventListener('click', () => this.closePopup({ dismiss: true }));

      this.boundDocumentKeydown = (event) => this.handleDocumentKeydown(event);
      this.boundDocumentClick = (event) => this.handleDocumentClick(event);
      if (this.isPopup) {
        document.addEventListener('keydown', this.boundDocumentKeydown);
        document.addEventListener('click', this.boundDocumentClick);
      }

      this.showIntro(false);

      if (this.isPopup) {
       this.scheduleAutoOpen();
      }
    }

    disconnectedCallback() {
      if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);
      if (this.boundDocumentKeydown) document.removeEventListener('keydown', this.boundDocumentKeydown);
      if (this.boundDocumentClick) document.removeEventListener('click', this.boundDocumentClick);
      if (this.popupIsOpen) {
        document.documentElement.classList.remove('budetz-style-finder-popup-lock');
        document.body.classList.remove('budetz-style-finder-popup-lock');
      }
    }

    disableWithError(message) {
      if (this.startButton) this.startButton.disabled = true;
      this.showGlobalStatus(message, true);
    }

    showIntro(focus = true) {
      this.intro.hidden = false;
      this.quiz.hidden = true;
      this.result.hidden = true;
      this.scrollDialogToTop();
      if (focus) this.startButton?.focus();
    }

    showQuestion(index, focus = true) {
      this.state.step = Math.max(0, Math.min(index, this.questions.length - 1));
      this.intro.hidden = true;
      this.quiz.hidden = false;
      this.result.hidden = true;
      this.clearGlobalStatus();

      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== this.state.step;
      });

      const currentQuestion = this.questions[this.state.step];
      const progressValue = this.state.step + 1;
      const isLastStep = progressValue === this.questions.length;
      const width = `${(progressValue / this.questions.length) * 100}%`;

      this.progress.setAttribute('aria-valuenow', String(progressValue));
      this.progressBar.style.width = width;
      this.progressName.textContent = 'STEP';
      this.progressCount.textContent = `${progressValue} OF ${this.questions.length}`;
      this.nextButton.textContent = isLastStep ? 'Build My Edit' : 'Continue';
      this.updateCurrentQuestionState();
      this.scrollDialogToTop();

      if (focus) {
        window.requestAnimationFrame(() => currentQuestion.querySelector('[data-bsf-step-heading]')?.focus());
      }
    }

    goBack() {
      if (this.state.step === 0) {
        this.showIntro();
        return;
      }
      this.showQuestion(this.state.step - 1);
    }

    goNext() {
      if (!this.currentAnswerIsValid()) {
        this.setQuestionError('Please choose an answer before continuing.');
        return;
      }

      if (this.state.step < this.questions.length - 1) {
        this.showQuestion(this.state.step + 1);
        return;
      }

      this.showResult();
    }

    handleAnswerChange(event) {
      const input = event.target.closest('.budetz-style-option__input');
      if (!input || !this.contains(input)) return;

      const question = input.closest('[data-bsf-question]')?.dataset.bsfQuestion;
      this.clearQuestionError(input.closest('[data-bsf-question]'));

      if (question === 'impression') {
        if (input.checked && !this.state.impressions.includes(input.value)) {
          if (this.state.impressions.length >= 2) {
            input.checked = false;
            this.setQuestionError(
              'You can choose up to two impressions.',
              input.closest('[data-bsf-question]')
            );
            this.updateImpressionCount();
            return;
          }

          this.state.impressions.push(input.value);
        } else if (!input.checked) {
          this.state.impressions = this.state.impressions.filter(
            (impression) => impression !== input.value
          );
       }

       this.updateImpressionCount();
       this.saveProfileState();

      } else if (question === 'intensity') {
        this.state.intensity = input.value;
        this.saveProfileState();
      } else if (question === 'occasion') {
         this.state.occasion = input.value;
      } else if (question === 'weather') {
        this.state.weather = input.value;
      }

      this.updateCurrentQuestionState();
    }

    updateImpressionCount() {
      const count = this.querySelector('[data-bsf-style-count]');
      if (!count) return;

      if (this.state.impressions.length === 0) {
        count.textContent = '0 of 2 selected';
        return;
      }

      if (this.state.impressions.length === 1) {
        count.textContent = '1 of 2 selected. Choose one more, or continue.';
        return;
      }

      count.textContent = '2 of 2 selected';
    }

    currentAnswerIsValid() {
      if (this.state.step === 0) {
        return this.state.impressions.length > 0 && this.state.impressions.length <= 2;
     }

      if (this.state.step === 1) {
        return Boolean(this.state.intensity);
     }

      if (this.state.step === 2) {
        return Boolean(this.state.occasion);
      }

      return Boolean(this.state.weather);
    }

    updateCurrentQuestionState() {
      if (this.nextButton) this.nextButton.disabled = !this.currentAnswerIsValid();
    }

    setQuestionError(message, question = this.questions[this.state.step]) {
      const error = question?.querySelector('[data-bsf-question-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    clearQuestionError(question = this.questions[this.state.step]) {
      const error = question?.querySelector('[data-bsf-question-error]');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    isValidScore(value) {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100
  );
}

getIntensityTarget() {
  const targets = {
    subtle: 25,
    noticeable: 55,
    statement: 85,
  };
    return targets[this.state.intensity];
}

passesAvailability(product) {
  return product?.available === true;
}

passesDataGate(product) {
  if (!product) return false;
  if (product.approved !== true) return false;
  if (product.status !== 'Ready') return false;

  const requiredScores = [
    product.style?.smart,
    product.style?.refined,
    product.style?.soft,
    product.style?.romantic,
    product.style?.streetwear,
    product.style?.bold,
    product.intensity,
    product.occasion?.everyday,
    product.occasion?.date,
    product.occasion?.work,
    product.occasion?.special_occasion,
    product.stylingEase,
  ];

  if (!requiredScores.every((score) => this.isValidScore(score))) {
    return false;
  }

  if (!Array.isArray(product.weather) || product.weather.length === 0) {
    return false;
  }

  return true;
}

passesWeather(product) {
  if (!Array.isArray(product.weather) || !this.state.weather) {
    return false;
  }

  const selectedWeather =
    String(this.state.weather).toLowerCase();

  const compatibleWeather = {
    hot: ['hot', 'warm'],
    warm: ['hot', 'warm', 'mild'],
    mild: ['warm', 'mild', 'cool'],
    cool: ['mild', 'cool', 'cold'],
    cold: ['cool', 'cold'],
  };

  const allowed =
    compatibleWeather[selectedWeather] ||
    [selectedWeather];

  return product.weather.some((weather) =>
    allowed.includes(
      String(weather).toLowerCase()
    )
  );
}

passesStyleGate(product) {
  const selected = this.state.impressions;

  if (!Array.isArray(selected) || selected.length === 0) return false;

  if (selected.length === 1) {
    const score = product.style?.[selected[0]];
    return this.isValidScore(score) && score >= 45;
  }

  const firstScore = product.style?.[selected[0]];
  const secondScore = product.style?.[selected[1]];

  return (
    this.isValidScore(firstScore) &&
    this.isValidScore(secondScore) &&
    firstScore >= 35 &&
    secondScore >= 35
  );
}

passesOccasionGate(product) {
  const score = product.occasion?.[this.state.occasion];
  return this.isValidScore(score) && score >= 40;
}

calculateImageMatch(product) {
  const selected = this.state.impressions;

  if (selected.length === 1) {
    return product.style[selected[0]];
  }

  const firstScore = product.style[selected[0]];
  const secondScore = product.style[selected[1]];
  const average = (firstScore + secondScore) / 2;
  const minimum = Math.min(firstScore, secondScore);

  return average * 0.6 + minimum * 0.4;
}

calculateIntensityMatch(product) {
  const target = this.getIntensityTarget();

  if (!this.isValidScore(product.intensity) || typeof target !== 'number') {
    return null;
  }

  return 100 - Math.abs(product.intensity - target);
}

calculateOccasionMatch(product) {
  return product.occasion?.[this.state.occasion];
}

scoreProduct(product) {
  const imageMatch = this.calculateImageMatch(product);
  const occasionMatch = this.calculateOccasionMatch(product);
  const intensityMatch = this.calculateIntensityMatch(product);

  if (
    !this.isValidScore(imageMatch) ||
    !this.isValidScore(occasionMatch) ||
    !this.isValidScore(intensityMatch)
  ) {
    return null;
  }

  const finalMatch =
    imageMatch * 0.5 +
    occasionMatch * 0.3 +
    intensityMatch * 0.2;

  return {
    product,
    imageMatch,
    occasionMatch,
    intensityMatch,
    finalMatch,
  };
}

getRankedProducts() {
  return this.products
    .filter((product) => this.passesAvailability(product))
    .filter((product) => this.passesDataGate(product))
    .filter((product) => this.passesWeather(product))
    .filter((product) => this.passesStyleGate(product))
    .filter((product) => this.passesOccasionGate(product))
    .map((product) => this.scoreProduct(product))
    .filter(Boolean)
    .sort((a, b) => b.finalMatch - a.finalMatch);
}
normalizeTraitList(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => String(item).trim().toLowerCase())
    .filter(Boolean);
}

productsMeaningfullyDiffer(first, second) {
  if (!first || !second) return false;

  const firstSignature = String(first.signatureDetail || '').trim().toLowerCase();
  const secondSignature = String(second.signatureDetail || '').trim().toLowerCase();

  if (
    firstSignature &&
    secondSignature &&
    firstSignature !== secondSignature
  ) {
    return true;
  }

  const firstTraits = this.normalizeTraitList(first.visualTraits);
  const secondTraits = this.normalizeTraitList(second.visualTraits);

  if (
    firstTraits.some((trait) => !secondTraits.includes(trait)) ||
    secondTraits.some((trait) => !firstTraits.includes(trait))
  ) {
    return true;
  }

  const firstSilhouette = this.normalizeTraitList(first.silhouette);
  const secondSilhouette = this.normalizeTraitList(second.silhouette);

  if (
    firstSilhouette.some((item) => !secondSilhouette.includes(item)) ||
    secondSilhouette.some((item) => !firstSilhouette.includes(item))
  ) {
    return true;
  }

  if (
    first.productType &&
    second.productType &&
    String(first.productType).toLowerCase() !==
      String(second.productType).toLowerCase()
  ) {
    return true;
  }

  if (
    this.isValidScore(first.intensity) &&
    this.isValidScore(second.intensity) &&
    Math.abs(first.intensity - second.intensity) >= 15
  ) {
    return true;
  }

  return false;
}

buildRecommendationGroups() {
  const ranked = this.getRankedProducts();

  // 1. BEST MATCH
  // 점수 순서를 기본으로 유지하되,
  // 이미 선택된 Best 상품들과 의미 있는 차이가 있는 후보를 우선한다.
  const best = [];

  for (const candidate of ranked) {
    if (best.length === 0) {
      best.push(candidate);
      continue;
    }

    const isDiverseEnough = best.every((selected) =>
      this.productsMeaningfullyDiffer(candidate.product, selected.product)
    );

    if (isDiverseEnough) {
      best.push(candidate);
    }

    if (best.length >= 3) break;
  }

  // 다양성 조건 때문에 3개를 못 채웠으면
  // 남은 고득점 상품으로 채운다.
  if (best.length < 3) {
    for (const candidate of ranked) {
      if (best.some((item) => item.product.id === candidate.product.id)) {
        continue;
      }

      best.push(candidate);

      if (best.length >= 3) break;
    }
  }

  const bestIds = new Set(best.map((item) => item.product.id));

  const remaining = ranked.filter(
    (item) => !bestIds.has(item.product.id)
  );

  // 2. EASY CHOICE
  const easy = remaining
    .filter(
      (item) =>
        item.finalMatch >= 70 &&
        this.isValidScore(item.product.stylingEase) &&
        item.product.stylingEase >= 70
    )
    .map((item) => ({
      ...item,
      easyScore:
        item.finalMatch * 0.7 +
        item.product.stylingEase * 0.3,
    }))
    .sort((a, b) => b.easyScore - a.easyScore)
    .slice(0, 2);

  const easyIds = new Set(easy.map((item) => item.product.id));

  // 3. TRY SOMETHING NEW
  const hero = best[0];

  const trySomethingNew = remaining
    .filter((item) => !easyIds.has(item.product.id))
    .filter((item) => item.finalMatch >= 65)
    .filter(
      (item) =>
        hero &&
        this.productsMeaningfullyDiffer(
          item.product,
          hero.product
        )
    )
    .slice(0, 2);

  return {
    best,
    easy,
    try: trySomethingNew,
  };
}

buildRecommendationSnapshot(groups) {
  const serializeEntries = (
    entries,
    group,
    limit
  ) => {
    if (!Array.isArray(entries)) {
      return [];
    }

    return entries
      .slice(0, limit)
      .map((entry) => {
        const product = entry?.product;

        if (!product) {
          return null;
        }

        let productUrl = '';

        try {
          productUrl = new URL(
            product.url,
            window.location.origin
          ).toString();
        } catch (error) {
          productUrl = String(
            product.url || ''
          );
        }

        let imageUrl = '';

        try {
          imageUrl = product.image?.src
           ? new URL(
                product.image.src,
                window.location.origin
              ).toString()
            : '';
         } catch (error) {
             imageUrl = '';
         }

        return {
          id: String(product.id || ''),
          handle: String(
            product.handle || ''
          ),
          title: String(
            product.title || ''
          ),
          url: productUrl,
          image: imageUrl,
          why: String(
            this.getProductWhy(
              entry,
              group
            ) || ''
          ),
        };
      })
      .filter(Boolean);
  };

  return {
    best: serializeEntries(
      groups?.best,
      'best',
      3
    ),
    easy: serializeEntries(
      groups?.easy,
      'easy',
      2
    ),
    try: serializeEntries(
      groups?.try,
      'try',
      2
    ),
  };
}

formatProductPrice(product) {
  if (!product || typeof product.price !== 'number') return '';

  try {
    const currency =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.current ||
      'USD';

    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency,
      }
    ).format(product.price / 100);
  } catch (error) {
    return `$${(product.price / 100).toFixed(2)}`;
  }
}

getProductWhy(entry, group) {
  const product = entry?.product;
  if (!product) return '';

  const reason = String(
    product.reason || ''
  ).trim();

  const detail = String(
    product.signatureDetail || ''
  ).trim();

  const directionTitles =
    (this.state.impressions || [])
      .map(
        (key) =>
          this.config.profiles?.[key]?.title
      )
      .filter(Boolean);

  const direction =
    directionTitles.length > 0
      ? directionTitles.join(' + ')
      : 'selected';

  const contextualReason = reason.replace(
    /\ban everyday look\b/gi,
    'the look'
  );

  if (group === 'easy') {
    if (detail) {
      return `${detail} connects naturally with your ${direction} direction while keeping the piece easy to style.`;
    }

    if (contextualReason) {
      return `${contextualReason} It is also easy to work into a look.`;
    }

    return 'It supports your direction without making the outfit difficult to build.';
  }

  if (group === 'try') {
    if (detail) {
      return `${detail} gives your ${direction} direction a slightly different edge without taking it too far.`;
    }

    if (contextualReason) {
      return `${contextualReason} It offers a slightly different take on your direction.`;
    }

    return 'It gives you another interpretation of your direction without feeling disconnected from it.';
  }

  if (detail) {
    return `${detail} gives this piece a clear connection to your ${direction} direction.`;
  }

  if (contextualReason) {
    return contextualReason;
  }

  return 'Its overall visual direction makes it a strong match for your selections.';
}

createProductCard(entry, group = 'best', hero = false) {
  const product = entry?.product;
  if (!product) return null;

  const card = document.createElement('article');
  card.className = hero
    ? 'budetz-style-product-card budetz-style-product-card--hero'
    : 'budetz-style-product-card';

  const imageLink = document.createElement('a');
  imageLink.className = 'budetz-style-product-card__image-link';
  imageLink.href = product.url;

  if (product.image?.src) {
    const image = document.createElement('img');
    image.className = 'budetz-style-product-card__image';
    image.src = product.image.src;
    image.alt = product.image.alt || product.title || '';
    image.loading = hero ? 'eager' : 'lazy';
    image.decoding = 'async';

    imageLink.appendChild(image);
  }

  const body = document.createElement('div');
  body.className = 'budetz-style-product-card__body';

  const badge = document.createElement('p');
  badge.className = 'budetz-style-product-card__badge';

  if (hero) {
    badge.textContent = '#1 BEST MATCH';
  } else if (group === 'easy') {
    badge.textContent = 'EASY CHOICE';
  } else if (group === 'try') {
    badge.textContent = 'TRY SOMETHING NEW';
  } else {
    badge.textContent = 'BEST MATCH';
  }

  const title = document.createElement('h4');
  title.className = 'budetz-style-product-card__title';

  const titleLink = document.createElement('a');
  titleLink.href = product.url;
  titleLink.textContent = product.title;

  title.appendChild(titleLink);

  const price = document.createElement('p');
  price.className = 'budetz-style-product-card__price';
  price.textContent = this.formatProductPrice(product);

  const why = document.createElement('div');
  why.className = 'budetz-style-product-card__why';

  const whyLabel = document.createElement('strong');
  whyLabel.textContent = 'Why we picked it';

  const whyCopy = document.createElement('p');
  whyCopy.textContent = this.getProductWhy(entry, group);

  why.append(whyLabel, whyCopy);

  const viewLink = document.createElement('a');
  viewLink.className =
    'budetz-style-finder__button budetz-style-product-card__button';
  viewLink.href = product.url;
  viewLink.textContent = 'View Item';

  body.append(badge, title);

  if (price.textContent) {
    body.appendChild(price);
  }

  body.append(why, viewLink);

  if (product.image?.src) {
    card.append(imageLink, body);
  } else {
    card.appendChild(body);
  }

  return card;
}

renderProductEntries(container, entries, group, hero = false) {
  if (!container) return;

  container.replaceChildren();

  entries.forEach((entry, index) => {
    const card = this.createProductCard(
      entry,
      group,
      hero && index === 0
    );

    if (card) {
      container.appendChild(card);
    }
  });
}

renderRecommendationGroups(groups) {
  const noResults = this.querySelector('[data-bsf-no-results]');

  const heroSection = this.querySelector(
    '[data-bsf-best-hero-section]'
  );
  const heroContainer = this.querySelector(
    '[data-bsf-best-hero]'
  );

  const moreSection = this.querySelector(
    '[data-bsf-best-more-section]'
  );
  const moreContainer = this.querySelector(
    '[data-bsf-best-more]'
  );

  const easySection = this.querySelector(
    '[data-bsf-easy-section]'
  );
  const easyContainer = this.querySelector(
    '[data-bsf-easy-list]'
  );

  const trySection = this.querySelector(
    '[data-bsf-try-section]'
  );
  const tryContainer = this.querySelector(
    '[data-bsf-try-list]'
  );

  const best = Array.isArray(groups?.best)
    ? groups.best
    : [];

  const easy = Array.isArray(groups?.easy)
    ? groups.easy
    : [];

  const tryItems = Array.isArray(groups?.try)
    ? groups.try
    : [];

  const hasResults = best.length > 0;

  if (noResults) {
    noResults.hidden = hasResults;
  }

  if (heroSection) {
    heroSection.hidden = best.length === 0;
  }

  if (heroContainer) {
    this.renderProductEntries(
      heroContainer,
      best.slice(0, 1),
      'best',
      true
    );
  }

  const moreBest = best.slice(1);

  if (moreSection) {
    moreSection.hidden = moreBest.length === 0;
  }

  if (moreContainer) {
    this.renderProductEntries(
      moreContainer,
      moreBest,
      'best'
    );
  }

  if (easySection) {
    easySection.hidden = easy.length === 0;
  }

  if (easyContainer) {
    this.renderProductEntries(
      easyContainer,
      easy,
      'easy'
    );
  }

  if (trySection) {
    trySection.hidden = tryItems.length === 0;
  }

  if (tryContainer) {
    this.renderProductEntries(
      tryContainer,
      tryItems,
      'try'
    );
  }
}
showResult({ focus = true } = {}) {
  const primaryKey = this.state.impressions[0];
  const secondaryKey = this.state.impressions[1];

  const primary = this.config.profiles[primaryKey];
  const secondary = secondaryKey
    ? this.config.profiles[secondaryKey]
    : null;

  const intensity = this.config.intensities[this.state.intensity];
  const occasion = this.config.occasions[this.state.occasion];
  const weather = this.config.weather[this.state.weather];

  if (!primary || !intensity || !occasion || !weather) {
    this.showQuestion(0);
    this.setQuestionError('Please complete all four questions.');
    return;
  }

  const groups = this.buildRecommendationGroups();

  this.currentRecommendationGroups = groups;

  this.intro.hidden = true;
  this.quiz.hidden = true;
  this.result.hidden = false;

  this.markStyleFinderCompleted();

  this.clearGlobalStatus();

  if (this.isPopup && !this.popupIsOpen) {
    this.openPopup({ focus: false });
  }

  const title = this.querySelector('[data-bsf-result-title]');
  const description = this.querySelector('[data-bsf-result-description]');
  const primaryChip = this.querySelector('[data-bsf-result-primary]');
  const secondaryChip = this.querySelector('[data-bsf-result-secondary]');
  const intensityChip = this.querySelector('[data-bsf-result-intensity]');
  const occasionChip = this.querySelector('[data-bsf-result-occasion]');
  const weatherChip = this.querySelector('[data-bsf-result-weather]');

  if (title) {
    title.textContent = secondary
      ? `${primary.title} + ${secondary.title}`
      : primary.title;
  }

  let directionCopy = '';

  if (secondary) {
    directionCopy =
      `${primary.title} with a ${secondary.title.toLowerCase()} edge`;
  } else {
    directionCopy = primary.title;
  }

  if (this.state.intensity === 'subtle') {
    directionCopy +=
      ' — kept understated so the direction feels natural rather than forced.';
  } else if (this.state.intensity === 'noticeable') {
    directionCopy +=
      ' — enough character to feel intentional without taking over the look.';
  } else {
    directionCopy +=
      ' — with a stronger presence and a clearer point of view.';
  }

  if (description) {
    description.textContent = directionCopy;
  }

  if (primaryChip) {
    primaryChip.textContent = primary.title.toUpperCase();
  }

  if (secondaryChip) {
    secondaryChip.hidden = !secondary;
    secondaryChip.textContent = secondary
      ? secondary.title.toUpperCase()
      : '';
  }

  if (intensityChip) {
    intensityChip.textContent = intensity.label.toUpperCase();
  }

  if (occasionChip) {
    occasionChip.textContent = occasion.label.toUpperCase();
  }

  if (weatherChip) {
    weatherChip.textContent = weather.label.toUpperCase();
  }

  this.renderRecommendationGroups(groups);

  this.scrollDialogToTop();

  this.dispatchEvent(
    new CustomEvent('budetz:style-finder:result', {
      bubbles: true,
      detail: {
        primary: primaryKey,
        secondary: secondaryKey || null,
        intensity: this.state.intensity,
        occasion: this.state.occasion,
        weather: this.state.weather,
        recommendationCount:
          groups.best.length +
          groups.easy.length +
          groups.try.length,
      },
    })
  );

  if (focus && title) {
    window.requestAnimationFrame(() => title.focus());
  }
}
    renderResultImages(profile) {
      if (!this.resultGallery || !this.resultGalleryTrack) return;

      const images = Array.isArray(profile.resultImages) ? profile.resultImages.filter((image) => image?.src) : [];
      this.resultGalleryTrack.replaceChildren();
      this.resultGallery.hidden = images.length === 0;

      images.forEach((image, index) => {
        const card = document.createElement('figure');
        const picture = document.createElement('img');
        const caption = document.createElement('figcaption');

        card.className = 'budetz-style-result__gallery-card';
        picture.className = 'budetz-style-result__gallery-image';
        picture.src = image.src;
        picture.alt = image.alt || `${profile.title} outfit recommendation ${index + 1}`;
        picture.loading = index === 0 ? 'eager' : 'lazy';
        picture.decoding = 'async';
        if (image.width) picture.width = image.width;
        if (image.height) picture.height = image.height;

        caption.className = 'budetz-style-result__gallery-caption';
        caption.textContent = image.caption || `Look ${String(index + 1).padStart(2, '0')}`;
        card.append(picture, caption);
        this.resultGalleryTrack.appendChild(card);
      });
    }

    renderColors(colors) {
      const list = this.querySelector('[data-bsf-result-colors]');
      list.replaceChildren();
      colors.forEach((color) => {
        const item = document.createElement('li');
        item.className = 'budetz-style-result__swatch';
        item.style.setProperty('--bsf-swatch-color', color.hex);
        item.textContent = color.name;
        list.appendChild(item);
      });
    }

    renderPatterns(patterns) {
      const list = this.querySelector('[data-bsf-result-patterns]');
      list.replaceChildren();
      patterns.forEach((pattern) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const tag = document.createElement('small');
        name.textContent = pattern.name;
        tag.textContent = pattern.tag;
        item.append(name, tag);
        list.appendChild(item);
      });
    }

    renderItems(items) {
      const list = this.querySelector('[data-bsf-result-items]');
      list.replaceChildren();
      items.forEach((itemName) => {
        const item = document.createElement('li');
        item.textContent = itemName;
        list.appendChild(item);
      });
    }

    updateCollectionLinks(primary, secondary) {
      const links = {
        easy: primary.collections.easy,
        best: primary.collections.best,
        try: secondary ? secondary.collections.try : primary.collections.try,
      };
      const selectedIntensityTag = this.config.intensities[this.state.intensity]?.tag;
      const selectedOccasionTag = this.config.occasions[this.state.occasion]?.tag;
      const tagsByGroup = {
        easy: ['intensity-quiet', selectedOccasionTag],
        best: [selectedIntensityTag, selectedOccasionTag],
        try: ['intensity-statement', selectedOccasionTag],
      };

      Object.entries(links).forEach(([group, url]) => {
        const link = this.querySelector(`[data-bsf-collection-link="${group}"]`);
        if (!link || typeof url !== 'string') return;
        link.href = this.withCollectionTags(url, tagsByGroup[group]);
        const groupName = link.querySelector('strong')?.textContent || 'recommendations';
        const profileName = group === 'try' && secondary ? secondary.title : primary.title;
        link.setAttribute('aria-label', `${groupName}: shop ${profileName}`);
      });

      const tryCopy = this.querySelector('[data-bsf-try-copy]');
      tryCopy.textContent = secondary
        ? `Explore ${secondary.title} as a controlled second direction.`
        : `Push ${primary.title} one step beyond your usual choice.`;
    }

    withCollectionTags(url, tags) {
      const tagHandles = (tags || [])
        .filter(Boolean)
        .map((tag) =>
          String(tag)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
        )
        .filter(Boolean);

      if (tagHandles.length === 0) return url;

      try {
        const target = new URL(url, window.location.origin);
        const collectionPath = target.pathname.match(/^(.*\/collections\/[^/]+)(?:\/.*)?$/);
        if (!collectionPath) return url;
        target.pathname = `${collectionPath[1]}/${tagHandles.join('+')}`;
        return target.origin === window.location.origin
          ? `${target.pathname}${target.search}${target.hash}`
          : target.toString();
      } catch (error) {
        return url;
      }
    }

    async handleEmailSubmit(event) {
  event.preventDefault();

  if (!this.emailForm) return;
  if (this.isEmailSubmitting) return;

  this.hideEmailClientError();

  if (this.emailSuccess) {
    this.emailSuccess.hidden = true;
  }

  // 1. Style Finder 답변 확인
  if (!this.answersAreComplete()) {
    this.showEmailClientError(
      'Please complete the Style Finder before saving your edit.'
    );
    return;
  }

  // 2. 이메일 확인
  const emailInput =
    this.emailForm.querySelector('[data-bsf-email]');

  if (!emailInput) {
    this.showEmailClientError(
      'Email field could not be found.'
    );
    return;
  }

  if (!emailInput.checkValidity()) {
    emailInput.reportValidity();
    return;
  }

  // 3. 저장할 데이터 만들기

  const recommendations =
  this.buildRecommendationSnapshot(
    this.currentRecommendationGroups
  );

  const payload = {
    email: emailInput.value.trim(),
    impressions: [...this.state.impressions],
    intensity: this.state.intensity,
    occasion: this.state.occasion,
    weather: this.state.weather,
    recommendations,
    marketingConsent:
      this.emailConsent?.checked === true,
  };

  // 4. 버튼 잠그기
  const submitButton =
    this.emailForm.querySelector(
      'button[type="submit"]'
    );

  const originalButtonText =
  submitButton?.textContent || 'SEND MY EDIT';

this.isEmailSubmitting = true;

if (submitButton) {
  submitButton.disabled = true;
  submitButton.textContent = 'SENDING...';
  submitButton.setAttribute('aria-busy', 'true');
}

let submissionSucceeded = false;

  try {
    // 5. BUDETZ Worker로 전송
    const response = await fetch(
      '/apps/budetz-style-finder/save-edit',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok || !data?.ok) {
      throw new Error(
        data?.message ||
          'Unable to save your edit right now.'
      );
    }

    submissionSucceeded = true;

    // 6. 성공
    if (this.emailSuccess) {
      const successText =
        this.emailSuccess.querySelector('strong');

      if (successText) {
       successText.textContent =
          'YOUR EDIT IS ON THE WAY';
      }

      let emailAddress =
        this.emailSuccess.querySelector(
          '[data-bsf-email-success-address]'
        );

      if (!emailAddress) {
        emailAddress = document.createElement('span');
       emailAddress.setAttribute(
         'data-bsf-email-success-address',
          ''
        );
        this.emailSuccess.appendChild(emailAddress);
}

      emailAddress.textContent =
        `Sent to ${emailInput.value.trim()}`;

            this.emailSuccess.hidden = false;

            window.requestAnimationFrame(() => {
              this.emailSuccess.focus();
           });
         }
  } catch (error) {
    console.error(
      'BUDETZ Style Finder save error:',
      error
    );

    this.showEmailClientError(
      'We couldn’t send your edit. Please check your connection and try again.'
    );
  } finally {
  this.isEmailSubmitting = false;

  if (submitButton) {
    submitButton.removeAttribute('aria-busy');

    if (submissionSucceeded) {
      submitButton.disabled = true;
      submitButton.textContent = 'SENT';
    } else {
      submitButton.disabled = false;
      submitButton.textContent = 'TRY AGAIN';
    }
  }
}
}

    showEmailClientError(message) {
      if (!this.emailClientError) return;
      this.emailClientError.textContent = message;
      this.emailClientError.hidden = false;
    }

    hideEmailClientError() {
      if (!this.emailClientError) return;
      this.emailClientError.textContent = '';
      this.emailClientError.hidden = true;
    }

    answersAreComplete() {
     return (
        this.state.impressions.length > 0 &&
        this.state.impressions.length <= 2 &&
        Boolean(this.config.intensities[this.state.intensity]) &&
        Boolean(this.config.occasions[this.state.occasion]) &&
        Boolean(this.config.weather[this.state.weather])
      );
    }

saveProfileState() {
  try {
    const profileState = {
      version: 1,
      impressions: [...this.state.impressions],
      intensity: this.state.intensity,
    };

    localStorage.setItem(
      this.profileStorageKey,
      JSON.stringify(profileState)
    );
  } catch (error) {
    // localStorage를 사용할 수 없어도 Style Finder는 계속 작동
  }
}

loadProfileState() {
  try {
    const raw = localStorage.getItem(this.profileStorageKey);
    if (!raw) return;

    const saved = JSON.parse(raw);

    if (!saved || saved.version !== 1) return;

    if (Array.isArray(saved.impressions)) {
      const validImpressions = saved.impressions
        .filter((impression) => Boolean(this.config.profiles[impression]))
        .slice(0, 2);

      if (validImpressions.length > 0) {
        this.state.impressions = validImpressions;
      }
    }

    if (
      saved.intensity &&
      this.config.intensities[saved.intensity]
    ) {
      this.state.intensity = saved.intensity;
    }
  } catch (error) {
    // 저장값이 깨져 있어도 Style Finder는 정상 작동
  }
}

markStyleFinderCompleted() {
  try {
    localStorage.setItem(this.completedStorageKey, 'true');
  } catch (error) {
    // localStorage를 사용할 수 없어도 Style Finder는 계속 작동
  }
}

hasCompletedStyleFinder() {
  try {
    return localStorage.getItem(this.completedStorageKey) === 'true';
  } catch (error) {
    return false;
  }
}

    restoreInputsFromState() {
      this.querySelectorAll('.budetz-style-option__input').forEach((input) => {
        const question = input.closest('[data-bsf-question]')?.dataset.bsfQuestion;

       if (question === 'impression') {
         input.checked = this.state.impressions.includes(input.value);
       }

        if (question === 'intensity') {
         input.checked = this.state.intensity === input.value;
       }

        if (question === 'occasion') {
         input.checked = this.state.occasion === input.value;
        }

        if (question === 'weather') {
          input.checked = this.state.weather === input.value;
       }
     });

     this.updateImpressionCount();
    }

    restart() {
      this.state = {
        step: 0,
        impressions: [],
        intensity: '',
        occasion: '',
        weather: '',
      };

      this.currentRecommendationGroups = null;

      this.querySelectorAll('.budetz-style-option__input').forEach((input) => {
        input.checked = false;
      });
      this.questions.forEach((question) => this.clearQuestionError(question));
      this.updateImpressionCount();
      this.hideEmailClientError();
      if (this.emailSuccess) this.emailSuccess.hidden = true;
      if (this.emailFields) this.emailFields.hidden = false;
      if (this.emailConsent) this.emailConsent.checked = false;
      const emailInput = this.emailForm?.querySelector('input[type="email"]');
      if (emailInput) emailInput.value = '';

      const emailSubmitButton =
        this.emailForm?.querySelector(
         'button[type="submit"]'
        );

      if (emailSubmitButton) {
       emailSubmitButton.disabled = false;
       emailSubmitButton.textContent = 'SEND MY EDIT';
       emailSubmitButton.removeAttribute('aria-busy');
      }

      this.isEmailSubmitting = false;

      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // No cleanup is required when browser storage is unavailable.
      }
      this.showQuestion(0);
    }

    scheduleAutoOpen() {
  if (
    !this.isPopup ||
    this.dataset.autoOpen !== 'true' ||
    this.popupWasDismissed()
  ) return;

  if (window.Shopify?.designMode) return;

  const pageType = this.dataset.pageType;
  if (!['index', 'collection'].includes(pageType)) return;

  if (this.hasCompletedStyleFinder()) return;

  const delay = 6000;
  const startScrollY = window.scrollY;

  const cleanup = () => {
    if (this.autoOpenTimer) {
      window.clearTimeout(this.autoOpenTimer);
      this.autoOpenTimer = null;
    }

    window.removeEventListener('scroll', onScroll);
  };

  const triggerAutoOpen = () => {
    if (
      this.popupIsOpen ||
      this.popupWasDismissed() ||
      this.hasCompletedStyleFinder()
    ) {
      cleanup();
      return;
    }

    cleanup();
    this.openPopup();
  };

  const onScroll = () => {
    const scrollDistance = Math.abs(window.scrollY - startScrollY);

    if (scrollDistance >= 80) {
      triggerAutoOpen();
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  this.autoOpenTimer = window.setTimeout(
    triggerAutoOpen,
    delay
  );
}

    openPopup({ focus = true } = {}) {
      if (!this.isPopup || !this.popup || !this.dialog) return;
      if (this.autoOpenTimer) {
        window.clearTimeout(this.autoOpenTimer);
        this.autoOpenTimer = null;
      }

      if (!this.popupIsOpen) this.lastFocusedElement = document.activeElement;
      this.popup.hidden = false;
      this.popupIsOpen = true;
      this.popupOpenButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('budetz-style-finder-popup-lock');
      document.body.classList.add('budetz-style-finder-popup-lock');

      if (focus) {
        window.requestAnimationFrame(() => {
          const target = this.quiz.hidden && this.result.hidden ? this.startButton : this.dialog;
          target?.focus();
        });
      }
    }

    closePopup({ dismiss = false, restoreFocus = true } = {}) {
      if (!this.isPopup || !this.popup || !this.popupIsOpen) return;
      this.popup.hidden = true;
      this.popupIsOpen = false;
      this.popupOpenButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('budetz-style-finder-popup-lock');
      document.body.classList.remove('budetz-style-finder-popup-lock');
      if (dismiss) this.dismissAutoPopup();

      if (restoreFocus) {
        const focusTarget =
          this.lastFocusedElement &&
          this.lastFocusedElement !== document.body &&
          typeof this.lastFocusedElement.focus === 'function'
            ? this.lastFocusedElement
            : this.popupOpenButton;
        window.requestAnimationFrame(() => focusTarget?.focus());
      }
    }

    handleDocumentKeydown(event) {
      if (!this.popupIsOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePopup();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-bsf-open-style-finder]');
      if (!trigger || !this.isPopup) return;
      event.preventDefault();
      this.openPopup();
    }

    getFocusableElements() {
      if (!this.dialog) return [];
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(this.dialog.querySelectorAll(selector)).filter(
        (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null
      );
    }

    popupWasDismissed() {
      try {
        const dismissedUntil = Number(window.localStorage.getItem(this.dismissStorageKey) || 0);
        if (!dismissedUntil) return false;
        if (Date.now() < dismissedUntil) return true;
        window.localStorage.removeItem(this.dismissStorageKey);
        return false;
      } catch (error) {
        return false;
      }
    }

    dismissAutoPopup() {
      if (!this.isPopup) return;
      const days = Math.max(1, Number(this.dataset.dismissDays) || 7);
      try {
        window.localStorage.setItem(this.dismissStorageKey, String(Date.now() + days * 24 * 60 * 60 * 1000));
      } catch (error) {
        // The floating launcher remains available when browser storage is unavailable.
      }
    }

    scrollDialogToTop() {
      if (!this.isPopup || !this.dialogScroll) return;
      window.requestAnimationFrame(() => {
        this.dialogScroll.scrollTop = 0;
      });
    }

    showGlobalStatus(message, isError = false) {
      if (!this.globalStatus) return;
      this.globalStatus.textContent = message;
      this.globalStatus.hidden = false;
      this.globalStatus.style.color = isError ? '#861e1e' : '';
      this.globalStatus.style.backgroundColor = isError ? '#fff2f2' : '';
    }

    clearGlobalStatus() {
      if (!this.globalStatus) return;
      this.globalStatus.textContent = '';
      this.globalStatus.hidden = true;
      this.globalStatus.style.color = '';
      this.globalStatus.style.backgroundColor = '';
    }
  }

  customElements.define('budetz-style-finder', BudetzStyleFinder);
}
